import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { saveUserBet, getPendingEvBets } from '../services/userDataService'
import { BOOKMAKER_LOGOS } from '../config/bookmakers'
import { playerImageUrlFromSelection as getPlayerImageUrl } from '../config/playerImages'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const parseSelection = (sel) => {
  const m = sel.match(/^(.+?)\s+(over|under)\s+(.+)$/i)
  if (!m) return { name: sel, dir: null, line: null }
  return { name: m[1], dir: m[2].toLowerCase(), line: m[3] }
}


const formatDate = (ts) =>
  new Date(ts + 'Z').toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  })

const betId = (opp) => `${opp.selection}|${opp.market_type}|${opp.commence_time}`

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useTrackBet({ kellyFraction, bankroll, roundStakes, hideOpp }) {
  const { user } = useAuth()
  const [trackedBets,    setTrackedBets]    = useState(new Set())
  const [showTrackModal, setShowTrackModal] = useState(false)
  const [trackingOpp,    setTrackingOpp]    = useState(null)
  const [trackForm,      setTrackForm]      = useState({})

  useEffect(() => {
    if (!user?.id) return
    // Every pending bet, not the 15 most recent: older ones used to lose
    // their tick and could be tracked a second time.
    getPendingEvBets(user.id).then(({ data }) => {
      if (!data) return
      const ids = data
        .filter(b => b.bet_data?.bet_type_tag === 'prop_ev')
        .map(b => betId(b.bet_data))
      setTrackedBets(new Set(ids))
    })
  }, [user])

  const computeRawStake = (kellyStake) => {
    const fractions = { '1/4': 0.25, '1/2': 0.5, '3/4': 0.75, 'Full': 1 }
    const frac = fractions[kellyFraction] ?? 0.5
    let stake = (kellyStake / 100) * frac * bankroll
    if (roundStakes === '5') return Math.round(stake / 5) * 5
    if (roundStakes === '1') return Math.round(stake)
    return parseFloat(stake.toFixed(2))
  }

  const isTracked = (opp) => trackedBets.has(betId(opp))

  const openTrackModal = (opp) => {
    setTrackingOpp(opp)
    setTrackForm({
      bookmaker:     opp.bookmakers[0]?.name || '',
      odds:          opp.original_odds,
      stake:         computeRawStake(opp.kelly_stake),
      ev_percentage: opp.ev_percentage,
      // Off on every open: hiding a whole match is a per-bet choice.
      hide_match:    false,
    })
    setShowTrackModal(true)
  }

  const closeTrackModal = () => {
    setShowTrackModal(false)
    setTrackingOpp(null)
    setTrackForm({})
  }

  const saveTrack = async (andHide = false) => {
    if (!user?.id) { alert('Please log in to track bets'); return }

    // The engine builds match as "{home} vs {away}", so this split is safe.
    const [homeTeam, awayTeam] = (trackingOpp.match || '').split(' vs ')

    const newBet = {
      type:          'EV',
      bet_type_tag:  'prop_ev',
      sport:         trackingOpp.sport,
      match:         trackingOpp.match,
      selection:     trackingOpp.selection,
      bookmaker:     trackForm.bookmaker,
      market_type:   trackingOpp.market_type,
      commence_time: trackingOpp.commence_time,
      // Raw values behind the display selection, kept so results settlement never
      // has to parse the sentence back apart. Absent on older opportunities, in
      // which case the bet simply stays manual.
      point:            trackingOpp.point ?? null,
      side:             trackingOpp.side ?? null,
      base_market_type: trackingOpp.base_market_type ?? null,
      player_name:      trackingOpp.player_name ?? null,
      team_name:        trackingOpp.team_name ?? null,
      home_team:        homeTeam || null,
      away_team:        awayTeam || null,
      odds:          parseFloat(trackForm.odds),
      stake:         parseFloat(trackForm.stake),
      ev_percentage: parseFloat(trackForm.ev_percentage),
      bookmaker_count: trackingOpp.bookmaker_count ?? null,
      status:        'pending',
      actual_profit: null,
      // The moment it was tracked. The game's own time is commence_time above,
      // which is what the tracker shows as the bet's date.
      placed_at:     new Date().toISOString(),
      settled_at:    null,
    }
    const { error } = await saveUserBet(user.id, newBet)
    if (error) { alert('Error saving bet. Please try again.'); return }
    setTrackedBets(prev => new Set([...prev, betId(trackingOpp)]))
    // The match option covers this selection too, so it wins over a plain hide.
    if (hideOpp) {
      if (trackForm.hide_match) hideOpp(trackingOpp, { wholeMatch: true })
      else if (andHide) hideOpp(trackingOpp)
    }
    closeTrackModal()
  }

  return { isTracked, openTrackModal, closeTrackModal, saveTrack, showTrackModal, trackingOpp, trackForm, setTrackForm }
}

// ─── Modal ────────────────────────────────────────────────────────────────────

export default function TrackBetModal({ isOpen, opp, form, onFormChange, onConfirm, onConfirmAndHide, onClose }) {
  const [bookieOpen, setBookieOpen] = useState(false)

  if (!isOpen || !opp) return null

  const { name, dir, line } = parseSelection(opp.selection)
  const patch = (field, val) => onFormChange({ ...form, [field]: val })
  const evClass = opp.ev_percentage >= 10 ? 'bc-ev-high' : opp.ev_percentage >= 5 ? 'bc-ev-mid' : 'bc-ev-low'

  return (
    <>
      <div className="bc-overlay" onClick={onClose} />
      <div className="ptm-modal">

        {/* Header */}
        <div className="ptm-header">
          <span className="ptm-title">Track Bet</span>
          <button className="bc-panel-close" onClick={onClose}>×</button>
        </div>

        {/* Player info card */}
        <div className="ptm-info-card">
          <div className="ptm-avatar-wrap">
            <img
              src={getPlayerImageUrl(opp.selection, opp.sport)}
              alt=""
              className="ptm-avatar"
              onLoad={e  => { e.target.style.opacity = '1'; e.target.parentNode.classList.remove('bc-avatar-missing') }}
              onError={e => { e.target.style.opacity = '0'; e.target.parentNode.classList.add('bc-avatar-missing') }}
            />
          </div>
          <div className="ptm-info-body">
            <div className="ptm-player-row">
              <span className="ptm-player-name">{name}</span>
              <span className={`bc-ev-badge ${evClass} ptm-ev-inline`}>
                +{opp.ev_percentage?.toFixed(2)}%
              </span>
            </div>
            {dir && (
              <div className="ptm-selection-row">
                <span className={`bc-sel-badge ${dir}`}>
                  <span className="bc-sel-badge-dir">{dir.charAt(0).toUpperCase() + dir.slice(1)}</span>
                  <span className="bc-sel-badge-num">{line}</span>
                </span>
              </div>
            )}
            <div className="ptm-info-sub">{opp.match}</div>
            <div className="ptm-info-meta">{formatDate(opp.commence_time)}</div>
          </div>
        </div>

        {/* Form */}
        <div className="ptm-form">

          {/* Bookmaker */}
          <div className="ptm-field-full">
            <label className="ptm-label">Bookmaker</label>
            {opp.bookmakers.length === 1 ? (
              <div className="ptm-bookie-display">
                {BOOKMAKER_LOGOS[form.bookmaker] && (
                  <img src={BOOKMAKER_LOGOS[form.bookmaker]} alt="" className="ptm-bookie-logo" />
                )}
                <span className="ptm-bookie-name">{form.bookmaker}</span>
              </div>
            ) : (
              <div className="ptm-bookie-select">
                <button
                  type="button"
                  className={`ptm-bookie-trigger${bookieOpen ? ' open' : ''}`}
                  onClick={() => setBookieOpen(o => !o)}
                >
                  {BOOKMAKER_LOGOS[form.bookmaker] && (
                    <img src={BOOKMAKER_LOGOS[form.bookmaker]} alt="" className="ptm-bookie-logo" />
                  )}
                  <span className="ptm-bookie-name">{form.bookmaker}</span>
                  <svg className={`ptm-bookie-chevron${bookieOpen ? ' open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                {bookieOpen && (
                  <>
                    <div className="ptm-bookie-backdrop" onClick={() => setBookieOpen(false)} />
                    <div className="ptm-bookie-dropdown">
                      {opp.bookmakers.map(b => (
                        <button
                          key={b.name}
                          type="button"
                          className={`ptm-bookie-option${form.bookmaker === b.name ? ' active' : ''}`}
                          onClick={() => { patch('bookmaker', b.name); setBookieOpen(false) }}
                        >
                          {BOOKMAKER_LOGOS[b.name] ? (
                            <img src={BOOKMAKER_LOGOS[b.name]} alt="" className="ptm-bookie-logo" />
                          ) : (
                            <div className="ptm-bookie-logo-placeholder" />
                          )}
                          <span className="ptm-bookie-name">{b.name}</span>
                          {form.bookmaker === b.name && (
                            <svg className="ptm-bookie-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Odds + Stake */}
          <div className="ptm-form-row">
            <div className="ptm-field">
              <label className="ptm-label">Odds</label>
              <input
                type="number"
                step="0.01"
                value={form.odds}
                onChange={e => patch('odds', e.target.value)}
                className="ptm-input"
              />
            </div>
            <div className="ptm-field">
              <label className="ptm-label">Stake ($)</label>
              <input
                type="number"
                value={form.stake}
                onChange={e => patch('stake', e.target.value)}
                className="ptm-input"
              />
            </div>
          </div>

          <label className="ptm-check">
            <input
              type="checkbox"
              checked={!!form.hide_match}
              onChange={e => patch('hide_match', e.target.checked)}
            />
            <span>Hide the rest of this match after tracking</span>
          </label>

        </div>

        {/* Footer */}
        <div className="ptm-footer">
          <button className="bc-btn ghost" onClick={onClose}>Cancel</button>
          <div className="ptm-footer-right">
            <button className="bc-btn danger" onClick={onConfirmAndHide}>Track & Hide</button>
            <button className="bc-btn primary" onClick={onConfirm}>Track</button>
          </div>
        </div>

      </div>
    </>
  )
}
